import { useEffect } from 'react';
import type { ProductBundle, ProductWithBundleId } from '~/models/CampaignModel';
import { FlowMode } from '~/models/FlowMode';
import { ProductType } from '~/models/product/ProductType';
import type { ProductTypeConfig } from '~/models/product/ProductTypeConfig';
import { BYOD_PRODUCT_ID } from '~/store/campaign/byod.constants';
import ProductGroupHeader from '../../campaign/components/product-group/components/product-group-header/ProductGroupHeader';
import { ByodBundle } from '../components/ByodBundle';
import { ProductBundleNextRowButton } from '../components/ProductBundleNextRowButton';
import { ProductBundleV2 } from '../components/ProductBundleV2';
import { useBasket } from '../useBasket';
import { useMappedBundles } from '../useMappedBundles';
import { getFlowMode } from '../v2.utils';

import '~/components/routes/campaign/components/product-group/product-group.scss';
import ErrorLabel from '~/components/shared/error-label/ErrorLabel';
import ProductLimit from '~/components/shared/product-limit/ProductLimit';

export const ProductGroupV2: React.FC<{ group: ProductBundle<ProductWithBundleId>[]; config: ProductTypeConfig; type: ProductType }> = ({
  group,
  config,
  type,
}) => {
  const selectedProducts = useBasket((s) => s.selectedProducts);
  const stepError = useBasket((s) => s.stepError);
  const setStepError = useBasket((s) => s.setStepError);
  const mappedGroup = useMappedBundles(group);
  useInitBundle(group, type, selectedProducts);

  // reset error for receivers on product change
  if (stepError && selectedProducts.some((p) => p.productType === ProductType.Receiver)) {
    setStepError(undefined);
  }
  const shouldShowProductLimit = group.length === mappedGroup.reduce((acc, bundle) => acc + bundle.selectedInBundle.length, 0);

  return (
    <div className="product-group" data-type={type}>
      <ProductGroupHeader config={config} type={type} selectedProducts={selectedProducts} />
      {/* Add separate selection option for receivers where users can select NOT to buy/rent a box */}
      {type === ProductType.Receiver && (
        <>
          <ByodBundle group={group} />
          <ErrorLabel hasError={!!stepError} focusOnError={true}>
            {stepError}
          </ErrorLabel>
        </>
      )}

      {/* PRODUCT BUNDLES */}
      {mappedGroup.map((mappedBundle, index) => (
        <ProductBundleV2
          key={mappedBundle.bundle.id}
          mappedGroup={mappedBundle}
          config={config}
          type={type}
          index={index}
          previousBundleId={mappedGroup[index - 1]?.bundle.id}
        />
      ))}
      <ProductBundleNextRowButton label={config?.buttonLabel || 'Jeg trenger et produkt til'} group={group} />
      {shouldShowProductLimit && <ProductLimit type={type} />}
    </div>
  );
};

const useInitBundle = (group: ProductBundle<ProductWithBundleId>[], type: ProductType, selectedProducts: ProductWithBundleId[]) => {
  const showBundle = useBasket((s) => s.showBundle);
  const openBundle = useBasket((s) => s.openBundle);
  const setActiveBundle = useBasket((s) => s.setActiveBundle);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We dont want this to run every time selected products change
  useEffect(() => {
    if (group.length === 0) return;

    /* Sets up initial configurations based on flowMode */
    const flowMode = getFlowMode(type);
    if (flowMode === FlowMode.Progressive) {
      // Close and hide all bundles on initialization
      group.forEach((bundle) => {
        openBundle(bundle.id, false);
        showBundle(bundle.id, false);
      });

      // Leave all receiver bundles closed when BYOD is selected
      if (type === ProductType.Receiver && selectedProducts.some((p) => p.id === BYOD_PRODUCT_ID)) {
        return;
      }

      const selectedBundles = group.filter((bundle) => bundle.offers.some((o) => selectedProducts.some((p) => p.id === o.id)));
      // If none are selected before
      if (selectedBundles.length === 0) {
        // if (selectors.hasSelectedBYOD && type === ProductType.Receiver) return;
        openBundle(group[0].id, true);
        showBundle(group[0].id, true);
        setActiveBundle(type, group[0].id);
        return;
      }

      // Show all bundles with selected products
      selectedBundles.forEach((bundle) => showBundle(bundle.id, true));
      // Set the last bundle of those to the active one
      setActiveBundle(type, selectedBundles[selectedBundles.length - 1].id);
    }

    if (flowMode === FlowMode.Open) {
      group.forEach((bundle) => {
        showBundle(bundle.id, true);
        openBundle(bundle.id, true);
      });
      setActiveBundle(type, group[group.length - 1].id);
    }
  }, [group, type, showBundle, openBundle, setActiveBundle]);
};
